import { supabase } from './supabase'
import { viewingAs } from './viewAsState'

/**
 * Who the signed-in session actually is on the platform.
 *
 * A valid Supabase session is NOT the same thing as a person. The link is
 * identity.auth_user_id, populated by the migration 010 trigger at first
 * sign-in. If that link is missing, app.current_person_id() returns null,
 * no tenant membership resolves, and every RLS policy denies — which looks
 * like an empty database rather than a missing row. So `person` being null
 * here is a real, reportable state, not an edge case.
 */
export type Me = {
  personId: string
  displayName: string
  givenName: string | null
  locale: string
  timezone: string | null
  /** 168. Languages shown as written rather than translated. Optional so the
   *  suite's fixtures need not grow; absent means none. */
  readAsWritten?: string[]
  email: string
  isVerified: boolean
  tenants: { id: string; slug: string; name: string }[]
  roles: { key: string; name: string; tenantId: string; orgUnitId: string | null }[]
  /**
   * Every permission key this person's roles carry, flattened.
   *
   * This exists so no surface has to say `roles.includes('comms_admin')`. A
   * role list is a proxy for capability and it goes wrong in both directions:
   * 015 added content.post to `employee`, which would have left every
   * role-name check in the app still hiding the composer from the ~900 people
   * it was written for; and `tenant_admin` holds neither content.post nor
   * content.publish, so a role-name check reading "admin means yes" would show
   * a composer that RLS then refuses.
   *
   * It is read from role_assignment → role → role_permission, which are the
   * same three tables app.has_permission() reads. Same source, so the button
   * and the policy cannot disagree.
   *
   * It is a CONVENIENCE, never a gate. RLS is the gate. Hiding a control the
   * database would refuse is courtesy; showing one it would allow is harmless.
   */
  permissions: Set<string>
  /**
   * Whether this person runs at least one stock room.
   *
   * NOT a permission, and it cannot be one. 064 made the managers a MEMBERSHIP
   * LIST rather than a 024 scoped grant, because the rooms one person runs need
   * not share a branch of the org tree — "Lima and Santiago but not Burnaby" is
   * a sentence no ancestor walk can express. There is deliberately no
   * `stock_manager` role either: a role meaning "is in that list" would be
   * `content_creator` again.
   *
   * The consequence is that `permissions` cannot answer "should this person see
   * the shop section", so the shell would hide it from exactly the people whose
   * job it is. Hence one boolean, read in the SAME batch as the roles rather
   * than as a fourth wave — OPS-28's objection is to sequential round trips on
   * a cold load, not to the number of rows.
   */
  managesStockRoom: boolean
  /**
   * 152. Whether the Gallery's browser view is open to this person: photo.browse
   * (gallery_browser, a pilot), photo.room, or photo_policy.browse_for_everyone
   * — the last is a COLUMN, not a permission, so `permissions` cannot answer it.
   * Read from may_browse_gallery(), app.may_browse_gallery()'s one door.
   * Optional so a fixture without it still means "only what the roles say".
   */
  browsesGallery?: boolean
  /**
   * 153. The permissions this person holds for the WHOLE company: carried by a
   * grant with no org unit. A regional HR Administrator holds admin.roles in
   * `permissions` (so People and roles opens for them) but not here, and every
   * company-wide control — the Gallery's browse-for-everyone switch, the
   * whole-company grant of a scopable role, Invite someone — reads this.
   * Optional so a fixture without it means "every grant is unscoped", which is
   * what every grant was before 153.
   */
  unscopedPermissions?: Set<string>
}

/** Does this person hold a permission? Presentation only — RLS decides. */
export function can(me: Me, permission: string): boolean {
  return me.permissions.has(permission)
}

/**
 * 153. Does this person hold a permission for the whole company, rather than
 * for a region? Presentation only — app.has_permission(tenant, perm) with no
 * org unit is the server's same question.
 */
export function canEverywhere(me: Me, permission: string): boolean {
  return (me.unscopedPermissions ?? me.permissions).has(permission)
}

/**
 * 152. Can this person open Photos → Gallery at all — as a browser, a document
 * user or an admin. The nav item and the page read this; which VIEW they get
 * is the page's (photo.room or not). Presentation only.
 */
export function canGallery(me: Me): boolean {
  return me.browsesGallery === true || can(me, 'photo.browse') || can(me, 'photo.room')
}

type IdentityRow = {
  identifier: string
  is_verified: boolean
  person: {
    id: string
    display_name: string
    given_name: string | null
    locale: string
    timezone: string | null
    read_as_written: string[] | null
  } | null
}

export async function loadMe(authUserId: string): Promise<Me | null> {
  // RLS on `identity` is self-only (policy identity_read_self), so this can
  // only ever return the caller's own rows. The eq() is belt-and-braces and
  // also picks the right row once a person has several identities — company
  // email, personal email, phone — which is the expected shape, not an
  // exception. See the `identity` table comment.
  // 176. Viewing as somebody: every read below is answered for THEM (the
  // server's app.current_person_id() is theirs), but the auth user id is still
  // the administrator's, so the identity cannot be found by it. Read the
  // person instead — many have no identity yet (never invited), and they are
  // exactly the people an administrator wants to look at.
  const viewing = viewingAs()
  const { data, error } = viewing
    ? await loadViewedIdentity(viewing.personId)
    : await supabase
        .from('identity')
        .select(
          'identifier, is_verified, person:person_id ( id, display_name, given_name, locale, timezone, read_as_written )',
        )
        .eq('auth_user_id', authUserId)
        .maybeSingle<IdentityRow>()

  if (error) throw error
  if (!data || !data.person) return null

  // The .eq('person_id') on both of these is load-bearing, and its absence is
  // not a syntax error — it is four identical tenants and somebody else's
  // roles rendered as yours.
  //
  // tenant_membership_read and role_assignment_read both scope to
  // app.is_tenant_member(tenant_id), NOT to self. That is deliberate: you have
  // to be able to see your colleagues. So an unfiltered select returns every
  // membership and every role assignment in the tenant, and for a four-person
  // tenant that renders as "ConeTec Group, ConeTec Group, ConeTec Group,
  // ConeTec Group" and three Comms Administrator badges that belong to
  // Chandra, Barbara and Jeff.
  //
  // This is rule 3 in a different costume: RLS decides what you MAY read, the
  // application decides what this screen is ABOUT. Narrowing to one person is
  // the app's job and will never be done for it.
  const [{ data: memberships, error: membershipError }, { data: assignments }, { data: rooms }, { data: browses }] = await Promise.all([
    supabase
      .from('tenant_membership')
      .select('tenant:tenant_id ( id, slug, name )')
      .eq('person_id', data.person.id)
      .eq('is_active', true),
    // role_permission comes back nested under the role. role_permission_read
    // admits any row whose role is a system role or belongs to a tenant you
    // are in, so this resolves — and it resolves to exactly what
    // app.has_permission() would compute from the same tables.
    supabase
      .from('role_assignment')
      .select('tenant_id, org_unit_id, role:role_id ( key, name, role_permission ( permission_key ) )')
      .eq('person_id', data.person.id),
    // 064's stock_room_manager_read is "your own rows, or shop.admin". The
    // .eq is still load-bearing for the second half of that policy: without it
    // a shop admin reads every manager row and this boolean stops being about
    // them — the same mistake the comment above describes for memberships.
    supabase
      .from('stock_room_manager')
      .select('stock_room_id')
      .eq('person_id', data.person.id)
      .limit(1),
    // 152. The Gallery's browse switch is a column (photo_policy), so it is
    // asked, not derived from the roles. A failed read is "no": the nav item
    // stays hidden and the page itself refuses or not (rule 12).
    supabase.rpc('may_browse_gallery'),
  ])

  // App.tsx reads "no active membership" as "your access is not active" (8 Oct),
  // so a FAILED read must not look like an empty one.
  if (membershipError) throw membershipError

  return {
    personId: data.person.id,
    displayName: data.person.display_name,
    givenName: data.person.given_name,
    locale: data.person.locale,
    timezone: data.person.timezone,
    readAsWritten: data.person.read_as_written ?? [],
    email: data.identifier,
    isVerified: data.is_verified,
    tenants: (memberships ?? [])
      .map((m: any) => m.tenant)
      .filter(Boolean) as Me['tenants'],
    roles: (assignments ?? [])
      .filter((a: any) => a.role)
      .map((a: any) => ({
        key: a.role.key,
        name: a.role.name,
        tenantId: a.tenant_id,
        orgUnitId: a.org_unit_id,
      })),
    permissions: new Set<string>(
      (assignments ?? [])
        .filter((a: any) => a.role)
        .flatMap((a: any) =>
          (a.role.role_permission ?? []).map((rp: any) => rp.permission_key as string),
        ),
    ),
    unscopedPermissions: new Set<string>(
      (assignments ?? [])
        .filter((a: any) => a.role && !a.org_unit_id)
        .flatMap((a: any) =>
          (a.role.role_permission ?? []).map((rp: any) => rp.permission_key as string),
        ),
    ),
    managesStockRoom: (rooms ?? []).length > 0,
    browsesGallery: browses === true,
  }
}

/**
 * 176. The IdentityRow for the person being viewed as: their person row (self
 * to RLS, since current_person_id() is theirs) and, if they have one, their
 * primary identity for the email line. No identity is not an error here.
 */
async function loadViewedIdentity(personId: string): Promise<{ data: IdentityRow | null; error: unknown }> {
  const [{ data: person, error }, { data: idn }] = await Promise.all([
    supabase
      .from('person')
      .select('id, display_name, given_name, locale, timezone, read_as_written')
      .eq('id', personId)
      .maybeSingle<NonNullable<IdentityRow['person']>>(),
    supabase
      .from('identity')
      .select('identifier, is_verified')
      .eq('person_id', personId)
      .order('is_primary', { ascending: false })
      .limit(1)
      .maybeSingle<{ identifier: string; is_verified: boolean }>(),
  ])
  if (error) return { data: null, error }
  if (!person) return { data: null, error: null }
  return {
    data: { identifier: idn?.identifier ?? '', is_verified: idn?.is_verified ?? false, person },
    error: null,
  }
}
